import React, { type FC, type ReactNode, memo, use, useCallback, useMemo } from "react";
import { Button, cn, Dropdown, type Key, Label, Separator, Spinner } from "@heroui/react";

import { FormattedMessage } from "react-intl";
import { KontoNumUtils } from "@binale-tech/shared";
import { useLocation, useNavigate } from "react-router-dom";

import { AppRoutes, getRoute } from "@domain/routing/routeConstants";
import { Category } from "@domain/models";
import { CompanyAccountingYearContext, CompanyContext } from "@domain/context/CompanyContext";
import { UserContext } from "@domain/context/UserProvider";

import { ChevronDown, Gear, StarFill } from "@gravity-ui/icons";

interface IProps {
    selectedRecordGroup: string;
}

const BankDropdownImpl: FC<IProps> = (props) => {
    const user = use(UserContext);
    const { isLoaded } = use(CompanyContext);
    const { yearConfig, yearBanks } = use(CompanyAccountingYearContext);
    const kontoExt = yearConfig?.kontoExt ?? 0;
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const handleAction = useCallback(
        (id: Key) => {
            if (id === AppRoutes.manageBanks) {
                return navigate(AppRoutes.manageBanks);
            }
            navigate(getRoute(AppRoutes.bankOverview, { uuid: id.toString() }));
        },
        [navigate],
    );

    const buttonText = useMemo(() => {
        let title: ReactNode = <FormattedMessage id="app.titles.Bank.pl" />;
        yearBanks.forEach((v) => {
            if (v.id === props.selectedRecordGroup) {
                title =
                    KontoNumUtils.getExtNumPrint(String(v.accountNum), kontoExt, Category.DefaultLen) + " - " + v.name;
            }
        });
        if (pathname === AppRoutes.manageBanks) {
            title = <FormattedMessage id="app.titles.settings.manage" />;
        }
        return title;
    }, [yearBanks, props.selectedRecordGroup, pathname, kontoExt]);

    const isDisabled = !isLoaded || !user.isUserDataLoaded;
    const selectedKeys = useMemo(() => [pathname], [pathname]);

    return (
        <Dropdown>
            <Dropdown.Trigger>
                <Button variant="outline" className="mr-2" isPending={!isLoaded} isDisabled={isDisabled}>
                    {({ isPending }) => (
                        <>
                            {isPending ? <Spinner color="current" size="sm" /> : null}
                            {buttonText}
                            <ChevronDown className="size-4" />
                        </>
                    )}
                </Button>
            </Dropdown.Trigger>
            <Dropdown.Popover>
                <Dropdown.Menu aria-label="Bank list" selectedKeys={selectedKeys} onAction={handleAction}>
                    <Dropdown.Section>
                        {yearBanks.map((b) => {
                            const isActive = b.id === props.selectedRecordGroup;
                            return (
                                <Dropdown.Item
                                    key={b.id}
                                    id={b.id}
                                    textValue={b.accountNum + " - " + b.name}
                                    className={cn("flex flex-row w-full", { "bg-accent/20": isActive })}
                                >
                                    <Label>{b.accountNum + " - " + b.name}</Label>
                                    <StarFill
                                        className={cn("size-4 text-yellow-500", { "opacity-0": !b.isFavorite })}
                                    />
                                </Dropdown.Item>
                            );
                        })}
                    </Dropdown.Section>
                    <Separator />
                    <Dropdown.Section>
                        <Dropdown.Item id={AppRoutes.manageBanks} textValue="Einstellungen">
                            <Label className="flex items-center gap-2">
                                <Gear />
                                <FormattedMessage id="app.titles.settings.manage" />
                            </Label>
                        </Dropdown.Item>
                    </Dropdown.Section>
                </Dropdown.Menu>
            </Dropdown.Popover>
        </Dropdown>
    );
};

export const BankDropdown = memo(BankDropdownImpl);
